import * as React from 'react';
import { cn } from '@/lib/utils';

export interface StatePanelProps {
  /**
   * `status` for the empty case (it is not an error), `alert` for the error. It
   * is the only thing that legitimately differs between the slot's two states.
   */
  role: 'status' | 'alert';
  children: React.ReactNode;
  className?: string;
}

/**
 * The box `EmptyState` and `ErrorState` draw.
 *
 * ## Why it exists
 *
 * Both occupy **the same slot** in `DataTable` (§5.1: it resolves the four
 * states in a single place): on one screen, the user sees one or the other in
 * the same gap depending on how the fetch turned out. Each had been built
 * separately and they chose different shapes — `EmptyState` with no frame and no
 * surface (`rounded-lg p-inset-lg`), `ErrorState` with both
 * (`rounded-lg border border-border bg-surface p-inset`) — so the empty table
 * and the errored table looked like two components from different families. It
 * was not a disagreement: it was an accident of having been written twice. With
 * a single box they cannot diverge again.
 *
 * ## What won, and why
 *
 * **`ErrorState`'s framed, surfaced shape wins**, with `EmptyState`'s generous
 * padding (`p-inset-lg`):
 *
 * 1. The slot replaces *a table's rows*, not a paragraph. With no frame, the
 *    content floats on the page and reads as "the table disappeared" instead of
 *    "the table has nothing to show". The frame is what preserves the place the
 *    table occupied.
 * 2. The surface separates the slot from the page background, which is what
 *    makes a centred block legible inside a card with `padding="none"` (the mode
 *    tables go full-bleed in, `card.tsx` §5.1).
 * 3. The large padding is `EmptyState`'s because this is a full page block, not
 *    an inline notice: `p-inset` (16px) squeezed the icon, the title and the
 *    action against the edge.
 *
 * ## Why it does NOT compose `Callout`
 *
 * `Callout` solves a different shape: an **inline** notice, left aligned, with
 * the icon to the side and the border/background tinted by `tone` — meant to be
 * interleaved among a screen's content (9 screens, §2.2). This slot is a
 * **centred** block replacing absent content. If `ErrorState` composed
 * `Callout`, it would end up tinted and left aligned while `EmptyState` — which
 * has its own `neutral`/`positive` tones, foreign to `Callout`'s four — stayed
 * centred and neutral: one divergence fixed by creating another, in the same
 * slot. The error's tone is communicated by the icon's colour, not by the box's.
 */
export function StatePanel({
  role,
  children,
  className,
}: StatePanelProps): React.JSX.Element {
  return (
    <div
      role={role}
      className={cn(
        'flex flex-col items-center justify-center gap-stack-sm rounded-lg border border-border bg-surface p-inset-lg text-center',
        className,
      )}
    >
      {children}
    </div>
  );
}
